import React from 'react';

/**
 * App-wide error boundary. Feb-2026: user reported that "pressing a
 * recording on mobile gives a red error and turns the screen white" —
 * that's React's default uncaught-error UI. This boundary catches any
 * render/effect crash below it and shows a friendly reload card
 * instead of the bare stack trace, so worst-case the user can tap
 * "Reload" and keep going.
 *
 * Wraps the whole router in App.js.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log so the error still shows up in dev tools + logging pipelines.
    console.error('[ErrorBoundary]', error, info?.componentStack);
    // Feb 2026 (user "Loading chunk … failed"): if a React.lazy chunk
    // failed to load (typically because bundle.js is from a previous
    // build and references chunk hashes that no longer exist on the
    // server), auto-clear caches and hard-reload once per session.
    const msg = (error && (error.message || String(error))) || '';
    if (/Loading chunk .* failed|ChunkLoadError|Loading CSS chunk .* failed/i.test(msg)) {
      const RELOAD_FLAG = 'vaad-chunk-reload';
      if (!sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        (async () => {
          try {
            if ('caches' in window) {
              const keys = await caches.keys();
              await Promise.all(keys.map((k) => caches.delete(k)));
            }
            if (navigator.serviceWorker) {
              const regs = await navigator.serviceWorker.getRegistrations();
              await Promise.all(regs.map((r) => r.unregister()));
            }
          } catch (_) { /* best effort */ }
          window.location.reload();
        })();
      }
    }
  }

  handleReload = () => {
    try {
      // Wipe any in-memory player state that might be poisoning the tree.
      window.location.reload();
    } catch {
      // ignore
    }
  };

  handleHome = () => {
    try {
      window.location.href = '/';
    } catch {
      // ignore
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const message =
      (this.state.error && (this.state.error.message || String(this.state.error))) || 'Unknown error';
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#0f1729] text-white px-6"
        data-testid="app-error-boundary"
      >
        <div className="max-w-md w-full rounded-2xl bg-white/5 border border-white/10 p-6 sm:p-8 text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-[#c9a43a]/20 flex items-center justify-center">
            <span className="text-2xl">!</span>
          </div>
          <h1 className="text-xl font-bold mb-2">Something went off-key</h1>
          <p className="text-white/70 text-sm mb-4">
            The app hit an unexpected error. Reload the page and it usually clears right up.
          </p>
          {process.env.NODE_ENV !== 'production' && (
            <pre className="text-left text-[11px] bg-black/40 rounded p-3 mb-4 overflow-auto max-h-40">
              {message}
            </pre>
          )}
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="app-error-reload-btn"
              className="px-4 py-2 rounded-md bg-[#9a7b24] hover:bg-[#7d6119] text-white font-semibold text-sm"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              data-testid="app-error-home-btn"
              className="px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white font-semibold text-sm"
            >
              Back home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
